import { ArrowDown, ArrowUp, ArrowUpDown, MoveHorizontal } from 'lucide-react';
import type { TreasuryRow } from '@odd/domain';
import { useErpLinks } from '../lib/erp';
import { treasuryEuro, treasuryMetric } from '../lib/treasuryPresentation';
import { ErpLink, SegmentBadge } from './ui';
import './TreasuryTable.css';

export type TreasurySortKey =
  | 'worst'
  | 'name'
  | 'bancos'
  | 'caja'
  | 'proveedores'
  | 'disponible'
  | 'gastoMes'
  | 'f1'
  | 'impagados'
  | 'f2';
const columns = [
  { key: 'name', label: 'Comunidad', unit: 'Código · segmento · AFF' },
  { key: 'bancos', label: 'Bancos', unit: '€' },
  { key: 'caja', label: 'Caja', unit: '€' },
  { key: 'proveedores', label: 'Proveedores', unit: 'Saldo en €' },
  { key: 'disponible', label: 'Disponible', unit: '€' },
  { key: 'gastoMes', label: 'Gasto mensual', unit: '€ / mes' },
  { key: 'f1', label: 'Cobertura', unit: 'Meses cubiertos' },
  { key: 'impagados', label: 'Impagados', unit: '€' },
  { key: 'f2', label: 'Morosidad', unit: '% del gasto anual' },
] as const;

function Metric({ row, metric }: { row: TreasuryRow; metric: 'f1' | 'f2' }) {
  const state = treasuryMetric(row, metric);
  return (
    <div className={`treasury-table-metric treasury-table-state--${state.light ?? 'none'}`}>
      <strong>{state.value}</strong>
      <span>
        {state.light && <i aria-hidden="true" />}
        {state.label}
      </span>
    </div>
  );
}

export function TreasuryTable({
  rows,
  sort,
  onSort,
  isLoading,
  view,
}: {
  rows: TreasuryRow[];
  sort: { key: TreasurySortKey; dir: 1 | -1 };
  onSort: (key: TreasurySortKey) => void;
  isLoading: boolean;
  view: 'out' | 'all';
}) {
  const erp = useErpLinks();
  const heading = (column: (typeof columns)[number]) => {
    const active = sort.key === column.key;
    const SortIcon = active ? (sort.dir === 1 ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th
        key={column.key}
        scope="col"
        rowSpan={column.key === 'name' ? 2 : undefined}
        aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
        className={column.key === 'name' ? 'treasury-table-community' : undefined}
      >
        <button
          type="button"
          onClick={() => onSort(column.key)}
          className={active ? 'is-sorted' : undefined}
          aria-label={`Ordenar por ${column.label.toLocaleLowerCase('es')}`}
        >
          <span>
            {column.label}
            <small>{column.unit}</small>
          </span>
          <SortIcon size={12} aria-hidden="true" />
        </button>
      </th>
    );
  };
  return (
    <section className="treasury-table-section" aria-label="Detalle financiero por comunidad">
      <div className="treasury-table-caption">
        <div>
          <h3>Detalle por comunidad</h3>
          <p>
            {rows.length} {rows.length === 1 ? 'comunidad' : 'comunidades'} en esta vista · importes en euros
          </p>
        </div>
        <span>
          <MoveHorizontal size={14} aria-hidden="true" />
          Desplaza para ver todas las columnas
        </span>
      </div>
      <div
        className="treasury-table-scroll"
        tabIndex={0}
        role="region"
        aria-label="Tabla de finanzas: desplazamiento horizontal y vertical"
      >
        <table className="treasury-table">
          <caption className="sr-only">
            Saldos, cobertura y morosidad de las comunidades de la vista seleccionada. Las columnas se pueden
            ordenar.
          </caption>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} className={`treasury-table-col--${c.key}`} />
            ))}
          </colgroup>
          <thead>
            <tr className="treasury-table-groups">
              {heading(columns[0])}
              <th scope="colgroup" colSpan={4}>
                Posición de caja
              </th>
              <th scope="colgroup" colSpan={2}>
                Cobertura de caja
              </th>
              <th scope="colgroup" colSpan={2}>
                Morosidad
              </th>
            </tr>
            <tr>{columns.slice(1).map(heading)}</tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.communityId}>
                <th scope="row" className="treasury-table-community">
                  <div className="treasury-table-identity">
                    <SegmentBadge segment={row.segment} />
                    <div>
                      <ErpLink href={erp.community(row.communityId)} title="Abrir la comunidad en Eleva">
                        {row.communityName}
                      </ErpLink>
                      <small>Código {row.code}</small>
                      <small>{row.affName ?? 'Sin AFF asignado'}</small>
                    </div>
                  </div>
                </th>
                {(['bancos', 'caja', 'proveedores', 'disponible', 'gastoMes'] as const).map((key) => (
                  <td
                    key={key}
                    className={`${row[key] < 0 ? 'treasury-table-negative' : ''} ${key === 'disponible' ? 'treasury-table-available' : ''}`}
                  >
                    {treasuryEuro(row[key])}
                  </td>
                ))}
                <td>
                  <Metric row={row} metric="f1" />
                </td>
                <td>{treasuryEuro(row.impagados)}</td>
                <td>
                  <Metric row={row} metric="f2" />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="treasury-table-empty" colSpan={9}>
                  {isLoading
                    ? 'Cargando…'
                    : view === 'out'
                      ? 'Ninguna comunidad fuera de umbral con este filtro.'
                      : 'Sin comunidades con este filtro.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="treasury-table-notes">
        <p>
          <strong>Proveedores:</strong> negativo = deuda; positivo = anticipos, que no se añaden al
          disponible.
        </p>
        <p>
          <strong>Sin base:</strong> faltan gastos para calcular el índice. Sin impagados, la morosidad
          aparece como «—».
        </p>
      </div>
    </section>
  );
}
